import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import Item from './Item';
import { IDynamicTableFilterState, IDynamicTableState } from '../../../interfaces/tables';
import { TABLE_REFUELINGS_IMPORT, TABLE_TRIP_BOOK } from '../../../Services/TableService';
import { setTableAllActiveRows, tablesAtom } from '../../../store/recoil/TableState';

interface IColumn {
  name: string;
  type: string;
  searchable: boolean;
  sortable: boolean;
  required: boolean;
  system: boolean;
  visible: boolean;
  title: string;
  filter?: IDynamicTableFilterState;
  active: boolean;
}

export interface IHeadProps {
  name?: string;
  actionsInMenu?: boolean;
  showFilters?: boolean;
  activePaging?: {
    currentPage: number;
    rowsPerPage: number;
    rowsTotal: number;
    pagesTotal: number;
  };
  activeRows?: number[];
  activeSorting?: {
    [key: string]: string;
  };
  key?: string;
  label?: boolean;
  save?: (
    name: string,
    columnId: string,
    filter: IDynamicTableFilterState,
    typeFilter: string,
    title: string
  ) => void;
  filterComponent?: IDynamicTableFilterState;
  columns?: IColumn;
  stateValue: null | string;
  parentState: any;
  actionOpen: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, title: string) => void;
  setValue: (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    key: string,
    val: string | number,
    name: string,
    columnId: string
  ) => void;
  actionSave: (columnId: string, value: any, e: any, filterType: string, title: string) => void;
  reset: (columns: IColumn, name: string, isEmpty: boolean) => void;
  tableName: string;
  openedMenuName: {
    [key: string]: boolean;
  };
  setFilter?: (
    name: string,
    columnId: string,
    filter: any,
    typeFilter: string,
    title: string
  ) => void;
  modalClassName?: boolean;
}

interface IDataProps {
  data: any;
}

type IComponentProps = IHeadProps & IDataProps;

const TableHead: React.FC<IComponentProps> = ({ ...props }) => {
  const setTables = useSetRecoilState<IDynamicTableState>(tablesAtom);

  const toFixTableHeader = () => {
    const rideDetailHeight = 0; // tslint:disable-line
    const pageWrapper = document.getElementById('page-content-wrapper-div');
    const tableWrapper = document.getElementById('tableWrapper');
    if (pageWrapper && tableWrapper) {
      const scroll = pageWrapper.scrollTop;
      let i = 0;
      const thElements: any = document.getElementsByClassName('th-sticky');
      if (scroll >= tableWrapper.offsetTop + rideDetailHeight) {
        for (i = 0; i < thElements.length; i += 1) {
          thElements[i].style.top = `${scroll - tableWrapper.offsetTop - 1 - rideDetailHeight}px`;
        }
        return false;
      }
      for (i = 0; i < thElements.length; i += 1) {
        thElements[i].style.top = 0;
      }
      return false;
    }
    return false;
  };

  React.useEffect(() => {
    const pageWrapper = document.getElementById('page-content-wrapper-div');
    if (pageWrapper) {
      pageWrapper.addEventListener('scroll', toFixTableHeader, { passive: true });
    }
    return () => {
      if (pageWrapper) {
        pageWrapper.removeEventListener('scroll', toFixTableHeader);
      }
    };
  }, []);

  const handleMultipleActions = () => {
    setTables((oldState) => setTableAllActiveRows(oldState, { name: props.tableName }));
  };

  React.useEffect(() => {
    const { data } = props;
    const { records, activeRows } = data;

    const checkbox: any = document.getElementById('dynamicTableMainCheckbox');
    if (checkbox) {
      if (activeRows.length === 0) {
        checkbox.indeterminate = false;
        checkbox.checked = false;
      } else if (records.length === activeRows.length && activeRows.length > 0) {
        checkbox.indeterminate = false;
        checkbox.checked = true;
      } else {
        checkbox.indeterminate = true;
      }
    }
  }, [props, props.data.activeRows.length]);

  const renderDefault = () => {
    const { data, tableName } = props;
    const {
      columns,
      records,
      activeRows,
      actions,
      sorting,
      paging,
      userSettingsKey,
      multipleActionsAllowed,
    } = data;

    const columnsLength = Object.keys(columns)
      .filter((j) => columns[j].system === false)
      .filter((j) => columns[j].visible === true).length;

    const rideIds: number[] = [];
    if (tableName === TABLE_TRIP_BOOK && records.length > 0) {
      Object.values(records).forEach((i: any) => {
        if (i.vehicles) {
          Object.values(i.vehicles).forEach((j: any) => {
            Object.values(j.records).forEach((k: any) => {
              rideIds.push(k.rideId);
            });
          });
        }
      });
    }

    let actionButtons: JSX.Element | JSX.Element[] | null = null;
    // 1 actions button
    if (
      records.filter((i: any) => i.actions && i.actions.length > 2).length > 0 ||
      userSettingsKey === 'list_config_ride_book_group_by_date' ||
      props.actionsInMenu
    ) {
      actionButtons = (
        <th
          className="w-sm text-center th-sticky sticky-col actions"
          style={{
            position: 'relative',
            top: '0px',
            zIndex: 1,
          }}
        >
          <FormattedMessage id="table.actionsBtn" />
        </th>
      );
    } else if (
      userSettingsKey !== 'list_config_ride_book_group_by_date' &&
      userSettingsKey !== 'list_config_ride_book_position'
    ) {
      // 2 actions (detail and delete)
      const withTwoActions = records.filter((i: any) => i.actions && i.actions.length === 2);
      if (withTwoActions.length > 0) {
        actionButtons = [
          <th
            key={`detail-${withTwoActions[0].actions[0].title}`}
            className={
              props.tableName === TABLE_REFUELINGS_IMPORT
                ? 'w-sm text-center th-sticky sticky-col import-detail'
                : 'w-sm text-center th-sticky sticky-col detail'
            }
          >
            {/* <FormattedMessage id={withTwoActions[0].actions[0].title} /> */}
            {withTwoActions[0].actions[0].title}
          </th>,
          <th
            key={`remove-${withTwoActions[0].actions[1].title}`}
            className="w-sm text-center th-w-sm text-center th-sticky sticky-col remove"
          >
            {/* <FormattedMessage id={withTwoActions[0].actions[1].title} /> */}
            {withTwoActions[0].actions[1].title}
          </th>,
        ];
      } else {
        // only 1 action
        const withOneAction = records.filter((i: any) => i.actions && i.actions.length === 1);
        if (withOneAction.length > 0) {
          actionButtons = (
            <th
              key={`remove-${withOneAction[0].actions[0].title}`}
              className="w-sm text-center th-sticky sticky-col"
            >
              {/* <FormattedMessage id={withOneAction[0].actions[0].title} /> */}
              {withOneAction[0].actions[0].title}
            </th>
          );
        }
      }
    }

    return (
      <thead id="tableThead" className="tableThead">
        <tr>
          {actions.multipleActions.length > 0 && multipleActionsAllowed && (
            <th className="w-sm th-sticky" style={{ zIndex: 1 }}>
              <div className="e-checkbox checkbox-sm">
                <label>
                  <input
                    id="dynamicTableMainCheckbox"
                    className="js-checkbox-indeterminate"
                    type="checkbox"
                    data-cy="checkbox-select-all"
                    onChange={handleMultipleActions}
                    checked={
                      tableName === TABLE_TRIP_BOOK
                        ? rideIds.length === activeRows.length && rideIds.length > 0
                        : records.length === activeRows.length && activeRows.length > 0
                    }
                  />
                  <span className="p-0" />
                </label>
              </div>
            </th>
          )}

          {Object.keys(columns)
            .filter((j) => columns[j].system === false)
            .filter((j) => columns[j].visible === true)
            .map((i, count) => (
              <Item
                stateValue={props.stateValue}
                parentState={props.parentState}
                data={data}
                actionOpen={props.actionOpen}
                setValue={props.setValue}
                actionSave={props.actionSave}
                reset={props.reset}
                name={props.name}
                key={i}
                id={i}
                label={true}
                activePaging={paging}
                activeRows={columns}
                activeSorting={sorting}
                sorting={sorting[columns[i].name]}
                save={props.setFilter}
                filterComponent={columns[i].filter}
                columns={columns[i]}
                openedMenuName={props.openedMenuName}
                tableName={props.tableName}
                countColumns={count + 1}
                columnsLength={columnsLength}
                modalClassName={props.modalClassName || false}
              />
            ))}
          {actionButtons}
        </tr>
      </thead>
    );
  };

  return renderDefault();
};

export default TableHead;
